import {
  ELEVATED_LOUNGES,
  SOURCECODE_WITH_ELEVATED_PERMISSION,
  SOURCECODE_WITH_STANDARD_PERMISSION,
} from 'config/productDefaultConfig';
import { BookingContext } from 'context/BookingContext';
import dayjs from 'dayjs';
import { useContext } from 'react';
import { formatEntitlementsDate } from 'utils/entitlements';

import useBookingDetails from './useBookingDetails';
import usePayload from './usePayload';
import useProduct from './useProduct';

const useEntitlements = () => {
  const { bookingDetailsData, loadingBookingDetails } = useBookingDetails();
  const {
    booking,
    entitlements: bookingEntitlements,
    loadingEntitlements,
    reloadEntitlements,
  } = useContext(BookingContext);
  const { entitlements: valuationEntitlements, loungeCode } = useProduct();
  const { sourceCode } = usePayload();

  const { departureDate, selectedEntitlementsCount, taxableGuestsCount } =
    booking;

  const entitlementsSource = valuationEntitlements ?? bookingEntitlements;

  const entitlemenstExpiryDate = formatEntitlementsDate(
    entitlementsSource[0]?.expiryDate
  );

  const entitlements = dayjs(departureDate).isBefore(
    entitlementsSource[0]?.expiryDate
  )
    ? entitlementsSource
    : [];

  const entitlementsCount = entitlements?.length ?? 0;

  const hasEntitlements = entitlementsCount > 0;

  const hasUnlimitedEntitlements = entitlements[0]?.isUnlimited;

  const usedEntitlementsCount =
    bookingDetailsData?.getBookingByID?.usedEntitlementsCount ?? 0;

  const availableEntitlements =
    (hasUnlimitedEntitlements ? 0 : usedEntitlementsCount) + entitlementsCount;
  const remainingEntitlementsCount =
    availableEntitlements - selectedEntitlementsCount;

  const maxEntitlementsCount = !hasUnlimitedEntitlements
    ? Math.min(taxableGuestsCount, availableEntitlements)
    : Math.max(taxableGuestsCount, availableEntitlements);

  const fetchingEntitlementsData = loadingEntitlements || loadingBookingDetails;

  const isElevatedLounge = ELEVATED_LOUNGES.includes(loungeCode);

  const hasStandardPermission = SOURCECODE_WITH_STANDARD_PERMISSION.includes(
    sourceCode ?? ''
  );

  const hasElevatedPermission = SOURCECODE_WITH_ELEVATED_PERMISSION.includes(
    sourceCode ?? ''
  );

  const hasStandardOrElevatedPermission =
    hasStandardPermission || hasElevatedPermission;

  const hasPermissionsForEntitlements =
    (isElevatedLounge && hasElevatedPermission) ||
    (!isElevatedLounge && hasStandardOrElevatedPermission);

  return {
    entitlemenstExpiryDate,
    entitlements,
    entitlementsCount,
    fetchingEntitlementsData,
    hasEntitlements,
    hasPermissionsForEntitlements,
    hasUnlimitedEntitlements,
    loadingEntitlements,
    maxEntitlementsCount,
    reloadEntitlements,
    remainingEntitlementsCount,
    usedEntitlementsCount,
  };
};

export default useEntitlements;
